import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, useFormikContext, getIn, setIn } from 'formik';
import Grid from '@material-ui/core/Grid';
import { Button } from '@material-ui/core';
import * as Yup from 'yup';
import { lookupsJson } from '@ourbranch/lookups';
import { appConfig } from 'brand-config';
import { v4 as uuidv4 } from 'uuid';

import {
  requiredString,
  requiredIfSectionTownshipRange,
  requiredIfLotBlock,
  isCountyRequired,
  requiredIfStreetAddress
} from 'common/helpers/yup-helpers';
import Field from 'core/components/form/form.v2';
import { Autocomplete } from 'core/components/autocomplete';
import { TextField } from 'core/components/text-field';
import { Tooltip } from 'core/components/tooltip';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import { useStore } from 'core/store';
import { SubRiskCollapsibleSection } from 'common/components/farm/locations/risks/sub-risks/sub-risk-collapsible-section';
import FormSectionWrapper from 'common/components/form-section-wrapper';
import CollapsibleListItem from 'common/components/farm/locations/risks/sub-risks/collapsible-list-item';
import CountiesAutocomplete from 'common/components/counties-autocomplete';
import AddressAutocomplete from 'core/components/address-autocomplete/address-autocomplete';
import caretOrange from 'core/assets/svg/ic-orange-caret-thick.svg';
import { buildPropertyOptions, getItemDisplayText, navigateToLocationOrRisk, buildItemLinkId } from '../helpers';
import { AppliedTag } from 'common/components/farm/applied-tag';
import useStyles from '../liability-coverages-styles';

const FIELD_NAME = 'farmLiabilityCoverage.additionalPropertiesExtensions';
const NOT_ON_POLICY_ID = 'notOnPolicy';

const DEFAULT_COVERAGE = 'residenceOccupiedByInsured';
const COVERAGE_OPTIONS = [{ value: DEFAULT_COVERAGE, label: 'Additional residences occupied by insured' }];

const NOT_ON_POLICY_TYPE_OPTIONS = [
  { id: 'location', value: 'Location', configKey: 'locations' },
  { id: 'residence', value: 'Residence', configKey: 'residences' }
];

const { coordinateOptions } = lookupsJson;
const { legalDescriptionFormatOptions } = lookupsJson.farm;

Yup.addMethod(Yup.string, 'requiredIfSectionTownshipRange', requiredIfSectionTownshipRange);
Yup.addMethod(Yup.string, 'requiredIfLotBlock', requiredIfLotBlock);
Yup.addMethod(Yup.string, 'isCountyRequired', isCountyRequired);
Yup.addMethod(Yup.string, 'requiredIfStreetAddress', requiredIfStreetAddress);
Yup.addMethod(Yup.string, 'requiredString', requiredString);

const formatPath = 'legalDescription.format';

const NOT_ON_POLICY_INITIAL_VALUES = {
  type: '',
  coverage: DEFAULT_COVERAGE,
  legalDescription: {
    format: undefined,
    section: undefined,
    township: undefined,
    townshipCoordinates: undefined,
    range: undefined,
    rangeCoordinates: undefined,
    lot: undefined,
    block: undefined
  },
  description: '',
  numberOfAcres: undefined,
  address: {
    address: undefined,
    address2: undefined,
    city: undefined,
    state: undefined,
    zip: undefined,
    county: undefined
  },
  additionalLegalDescription: undefined,
  locationDirections: undefined
};

const VALIDATION_SCHEMAS_BY_TYPE = {
  location: Yup.object().shape({
    legalDescription: Yup.object().shape({
      format: Yup.string().requiredString('Legal Description Format is required'),
      lot: Yup.string().requiredIfLotBlock('Lot is required', formatPath),
      block: Yup.string().requiredIfLotBlock('Block is required', formatPath),
      section: Yup.string().requiredIfSectionTownshipRange('Section is required', formatPath),
      township: Yup.string().requiredIfSectionTownshipRange('Township is required', formatPath),
      range: Yup.string().requiredIfSectionTownshipRange('Range is required', formatPath),
      townshipCoordinates: Yup.string(),
      rangeCoordinates: Yup.string()
    }),
    address: Yup.object().shape({
      address: Yup.string().requiredIfStreetAddress('Address is required', formatPath),
      address2: Yup.string().nullable(),
      city: Yup.string().required('City is required'),
      state: Yup.string().required('State is required'),
      zip: Yup.string().required('ZIP is required'),
      county: Yup.string().isCountyRequired('County is required', formatPath)
    }),
    description: Yup.string().required('Location name is required'),
    numberOfAcres: Yup.number().required('Acreage is required').integer('Must be a whole number')
  }),
  residence: Yup.object().shape({
    description: Yup.string().required('Item name is required'),
    address: Yup.object().shape({
      address: Yup.string().required('Address is required'),
      city: Yup.string().required('City is required'),
      state: Yup.string().required('State is required'),
      zip: Yup.string().required('ZIP is required')
    })
  })
};

const OccupiedAdditionalPropertiesExtensions = ({ disabled = false, onChangeActive }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();

  const allItems = getIn(values, FIELD_NAME) || [];
  const items = allItems.filter((item) => item.coverage === DEFAULT_COVERAGE);

  const liabilityType = getIn(values, 'farmLiabilityCoverage.type');
  const { inPolicyRoute } = useRouteHelper();
  const {
    account: {
      policies: {
        policy: { policy: policyData }
      }
    }
  } = useStore();
  const state = inPolicyRoute
    ? policyData?.state
    : getIn(values, 'correctedAddress.state') || getIn(values, 'farmLocations.0.address.state');
  const typeConfig = appConfig.farm?.[state]?.liabilityExtensions?.occupied?.[liabilityType] || {};

  const enabledTypes = ['locations', 'residences'].filter((type) => typeConfig[type]);
  const typeSelectOptions = NOT_ON_POLICY_TYPE_OPTIONS.filter((opt) => typeConfig[opt.configKey]);

  const propertyOptions = useMemo(
    () => buildPropertyOptions(values.farmLocations, typeConfig, state),
    [values.farmLocations, typeConfig, state]
  );

  const [inputValue, setInputValue] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(null);

  const handleSelectChange = useCallback((_event, val) => {
    setInputValue(val?.text || '');
    setSelectedProperty(val);
  }, []);

  const handleInputChange = useCallback((event) => {
    setInputValue(event.target.value);
    setSelectedProperty(null);
  }, []);

  const handleBlur = useCallback(() => {
    if (!selectedProperty) {
      setInputValue('');
    }
  }, [selectedProperty]);

  const handleNotOnPolicySubmit = useCallback(
    (formValues) => {
      const newItem = {
        id: uuidv4(),
        type: formValues.type,
        locationId: null,
        riskId: null,
        coverage: formValues.coverage
      };

      const address = formValues.address || {};
      newItem.description = formValues.description || null;
      newItem.addressDetails = {
        address: address.address || null,
        address2: address.address2 || null,
        city: address.city || null,
        state: address.state || null,
        zip: address.zip || null,
        county: address.county || null
      };

      if (formValues.type === 'location') {
        const legalDescriptionFormat = formValues.legalDescription?.format;
        if (legalDescriptionFormat !== 'street') {
          newItem.addressDetails.address = null;
          newItem.addressDetails.address2 = null;
        }
        newItem.legalDescription = formValues.legalDescription;
        newItem.additionalLegalDescription = formValues.additionalLegalDescription || null;
        newItem.locationDirections = formValues.locationDirections || null;
        newItem.numberOfAcres = formValues.numberOfAcres != null ? parseInt(formValues.numberOfAcres, 10) : null;
      }

      setFieldValue(FIELD_NAME, [...allItems, newItem]);
      setFieldTouched(FIELD_NAME);
      setSelectedProperty(null);
      setInputValue('');
      onChangeActive();
    },
    [allItems, setFieldValue, setFieldTouched, onChangeActive]
  );

  const addedIds = useMemo(
    () =>
      new Set(allItems.filter((item) => item.riskId || item.locationId).map((item) => item.riskId || item.locationId)),
    [allItems]
  );

  const availableOptions = useMemo(
    () =>
      propertyOptions.filter(
        (option) => option.id === NOT_ON_POLICY_ID || !addedIds.has(option.riskId || option.locationId)
      ),
    [propertyOptions, addedIds]
  );

  const filteredOptions = inputValue
    ? availableOptions.filter((option) => {
        const text = option.text || '';
        return (
          text.toLowerCase().includes(inputValue.toLowerCase()) ||
          option.id?.toString().toLowerCase().includes(inputValue.toLowerCase())
        );
      })
    : availableOptions;

  const currentValue = selectedProperty || (inputValue ? { id: inputValue, text: inputValue } : null);
  const inputValueProp = inputValue ? { inputValue } : {};

  if (!typeConfig.locations && !typeConfig.residences) {
    return null;
  }

  const enabledTypesLabel = enabledTypes.join(', ');

  return (
    <div className={classes.sectionContainer} id="farmLiabilityCoverage.occupiedAdditionalPropertiesExtensions">
      <SubRiskCollapsibleSection
        type="secondary"
        content={<div className={classes.headerText}>Additional {enabledTypesLabel} occupied by insured</div>}
        contentRight={items.length > 0 ? <AppliedTag /> : null}
        initialOpen={items.length > 0}
      >
        <FormSectionWrapper
          sectionTitle="Scheduled items"
          totalLabel="items"
          totalCount={items.length}
          emptyStateTitle="There are no items added yet"
          emptyStateSubtitle="To add one select from below"
        >
          {items.map((item) => {
            const displayText = getItemDisplayText(item, values.farmLocations, state);
            const isLocation = item.type === 'location';
            const isOnPolicy = item.riskId || item.locationId;
            const absoluteIndex = allItems.findIndex((i) => i.id === item.id);
            const itemPath = `${FIELD_NAME}[${absoluteIndex}]`;
            const title = (
              <div className={classes.itemTitleWrapper}>
                <div>{displayText}</div>
                <div className={classes.itemTypeLabel}>{item.type.toUpperCase()}</div>
              </div>
            );
            const onDelete = () => {
              const newItems = allItems.filter((i) => i.id !== item.id);
              setFieldValue(FIELD_NAME, newItems);
              setFieldTouched(FIELD_NAME);
              onChangeActive();
            };

            const coverageRadio = (
              <>
                <Grid item xs={12}>
                  <div className={classes.coverageLabel}>Coverage</div>
                </Grid>
                <Field
                  xs={12}
                  type="radio"
                  id={`${itemPath}.coverage`}
                  name={`${itemPath}.coverage`}
                  mode="dark"
                  options={COVERAGE_OPTIONS}
                  disabled={disabled}
                  labelClassName={classes.coverageRadioLabel}
                  optionsSideBySide={false}
                  onChange={onChangeActive}
                />
              </>
            );

            if (isOnPolicy) {
              const linkId = buildItemLinkId(item, values.farmLocations);
              return (
                <CollapsibleListItem
                  key={`occupied-property-${item.id}`}
                  title={title}
                  rightSideValues={[]}
                  onDelete={onDelete}
                  disabled={disabled}
                >
                  <Field
                    xs={12}
                    type="value"
                    id={`${itemPath}-details-link`}
                    name={`${itemPath}-details-link`}
                    mode="dark"
                    label="Details"
                    value={
                      <span className={classes.goToLink} onClick={() => navigateToLocationOrRisk(linkId)}>
                        {isLocation ? 'Go to location' : 'Go to risk'}
                        <img src={caretOrange} alt="" className={classes.goToLinkCaret} />
                      </span>
                    }
                  />
                  {coverageRadio}
                </CollapsibleListItem>
              );
            }

            const legalDescriptionFormat = item.legalDescription?.format;
            const itemState = item.addressDetails?.state || Object.keys(appConfig.farm || {})[0] || 'IL';

            return (
              <CollapsibleListItem
                key={`occupied-property-${item.id}`}
                title={title}
                rightSideValues={[
                  {
                    label: '',
                    value: (
                      <span className={classes.onlyCoveredHere}>
                        Only covered here
                        <Tooltip
                          text="This item is covered under liability only and does not appear elsewhere on the policy."
                          smallIcon
                        />
                      </span>
                    )
                  }
                ]}
                onDelete={onDelete}
                disabled={disabled}
              >
                {item.type === 'location' && (
                  <>
                    <Field
                      xs={4}
                      type="select"
                      name={`${itemPath}.legalDescription.format`}
                      mode="dark"
                      label="Legal description format*"
                      options={legalDescriptionFormatOptions}
                      onChange={(value) => {
                        if (value === 'section-township-range') {
                          setFieldValue(`${itemPath}.legalDescription.townshipCoordinates`, 'unknown');
                          setFieldValue(`${itemPath}.legalDescription.rangeCoordinates`, 'unknown');
                        }
                        onChangeActive();
                      }}
                      disabled={disabled}
                    />
                    <Field
                      xs={4}
                      type="numeric"
                      name={`${itemPath}.numberOfAcres`}
                      mode="dark"
                      label="Acres*"
                      disabled={disabled}
                      onChange={onChangeActive}
                    />
                    <Field
                      xs={4}
                      type="string"
                      name={`${itemPath}.description`}
                      mode="dark"
                      label="Location name*"
                      disabled={disabled}
                      onChange={onChangeActive}
                    />

                    {legalDescriptionFormat === 'street' && (
                      <>
                        <AddressAutocomplete
                          id={`${itemPath}.addressDetails.address`}
                          name={`${itemPath}.addressDetails.address`}
                          xs={6}
                          mode="dark"
                          label="Address*"
                          disabled={disabled}
                        />
                        <Field
                          xs={6}
                          type="string"
                          name={`${itemPath}.addressDetails.address2`}
                          mode="dark"
                          label="Apt, unit, building, etc."
                          disabled={disabled}
                          onChange={onChangeActive}
                        />
                        <Field
                          xs={4}
                          type="string"
                          name={`${itemPath}.addressDetails.city`}
                          mode="dark"
                          label="City*"
                          disabled={disabled}
                          onChange={onChangeActive}
                        />
                        <Field
                          xs={4}
                          type="state"
                          name={`${itemPath}.addressDetails.state`}
                          mode="dark"
                          label="State*"
                          disabled={disabled}
                          onChange={onChangeActive}
                        />
                        <Field
                          xs={4}
                          type="string"
                          name={`${itemPath}.addressDetails.zip`}
                          mode="dark"
                          label="Zip code*"
                          disabled={disabled}
                          onChange={onChangeActive}
                        />
                      </>
                    )}

                    {legalDescriptionFormat === 'section-township-range' && (
                      <>
                        <Field
                          xs={4}
                          type="string"
                          name={`${itemPath}.addressDetails.city`}
                          mode="dark"
                          label="City*"
                          disabled={disabled}
                          onChange={onChangeActive}
                        />
                        <Field
                          xs={4}
                          type="state"
                          name={`${itemPath}.addressDetails.state`}
                          mode="dark"
                          label="State*"
                          disabled={disabled}
                          onChange={onChangeActive}
                        />
                        <Field
                          xs={4}
                          type="string"
                          name={`${itemPath}.addressDetails.zip`}
                          mode="dark"
                          label="Zip code*"
                          disabled={disabled}
                          onChange={onChangeActive}
                        />
                        <CountiesAutocomplete
                          id={`${itemPath}.addressDetails.county`}
                          state={itemState}
                          setValues={setFieldValue}
                          mode="dark"
                          disabled={disabled}
                        />
                        <Field
                          xs={4}
                          type="string"
                          name={`${itemPath}.legalDescription.section`}
                          mode="dark"
                          label="Section*"
                          disabled={disabled}
                          onChange={onChangeActive}
                        />
                        <Field
                          xs={2}
                          type="string"
                          name={`${itemPath}.legalDescription.township`}
                          mode="dark"
                          label="Township*"
                          disabled={disabled}
                          onChange={onChangeActive}
                        />
                        <Field
                          xs={2}
                          type="select"
                          name={`${itemPath}.legalDescription.townshipCoordinates`}
                          mode="dark"
                          label="Coordinates"
                          options={coordinateOptions}
                          disabled={disabled}
                          onChange={onChangeActive}
                        />
                        <Field
                          xs={2}
                          type="string"
                          name={`${itemPath}.legalDescription.range`}
                          mode="dark"
                          label="Range*"
                          disabled={disabled}
                          onChange={onChangeActive}
                        />
                        <Field
                          xs={2}
                          type="select"
                          name={`${itemPath}.legalDescription.rangeCoordinates`}
                          mode="dark"
                          label="Coordinates"
                          options={coordinateOptions}
                          disabled={disabled}
                          onChange={onChangeActive}
                        />
                      </>
                    )}

                    {legalDescriptionFormat === 'lot-block' && (
                      <>
                        <Field
                          xs={6}
                          type="numeric"
                          name={`${itemPath}.legalDescription.lot`}
                          mode="dark"
                          label="Lot*"
                          disabled={disabled}
                          onChange={onChangeActive}
                        />
                        <Field
                          xs={6}
                          type="numeric"
                          name={`${itemPath}.legalDescription.block`}
                          mode="dark"
                          label="Block*"
                          disabled={disabled}
                          onChange={onChangeActive}
                        />
                        <Field
                          xs={4}
                          type="string"
                          name={`${itemPath}.addressDetails.city`}
                          mode="dark"
                          label="City*"
                          disabled={disabled}
                          onChange={onChangeActive}
                        />
                        <Field
                          xs={4}
                          type="state"
                          name={`${itemPath}.addressDetails.state`}
                          mode="dark"
                          label="State*"
                          disabled={disabled}
                          onChange={onChangeActive}
                        />
                        <Field
                          xs={4}
                          type="string"
                          name={`${itemPath}.addressDetails.zip`}
                          mode="dark"
                          label="Zip code*"
                          disabled={disabled}
                          onChange={onChangeActive}
                        />
                        <CountiesAutocomplete
                          id={`${itemPath}.addressDetails.county`}
                          state={itemState}
                          setValues={setFieldValue}
                          mode="dark"
                          disabled={disabled}
                        />
                      </>
                    )}

                    {legalDescriptionFormat === 'long-legal' && (
                      <>
                        <Field
                          xs={4}
                          type="string"
                          name={`${itemPath}.addressDetails.city`}
                          mode="dark"
                          label="City*"
                          disabled={disabled}
                          onChange={onChangeActive}
                        />
                        <Field
                          xs={4}
                          type="state"
                          name={`${itemPath}.addressDetails.state`}
                          mode="dark"
                          label="State*"
                          disabled={disabled}
                          onChange={onChangeActive}
                        />
                        <Field
                          xs={4}
                          type="string"
                          name={`${itemPath}.addressDetails.zip`}
                          mode="dark"
                          label="Zip code*"
                          disabled={disabled}
                          onChange={onChangeActive}
                        />
                        <CountiesAutocomplete
                          id={`${itemPath}.addressDetails.county`}
                          state={itemState}
                          setValues={setFieldValue}
                          mode="dark"
                          disabled={disabled}
                        />
                      </>
                    )}

                    {!!legalDescriptionFormat && (
                      <>
                        <Field
                          xs={12}
                          type="string"
                          name={`${itemPath}.additionalLegalDescription`}
                          mode="dark"
                          label="Additional legal description (optional)"
                          disabled={disabled}
                          onChange={onChangeActive}
                        />
                        <Field
                          xs={12}
                          type="string"
                          name={`${itemPath}.locationDirections`}
                          mode="dark"
                          label="Location directions (optional)"
                          disabled={disabled}
                          onChange={onChangeActive}
                        />
                      </>
                    )}
                  </>
                )}

                {item.type === 'residence' && (
                  <>
                    <Field
                      xs={12}
                      type="string"
                      name={`${itemPath}.description`}
                      mode="dark"
                      label="Item name*"
                      disabled={disabled}
                      onChange={onChangeActive}
                    />
                    <AddressAutocomplete
                      id={`${itemPath}.addressDetails.address`}
                      name={`${itemPath}.addressDetails.address`}
                      xs={6}
                      mode="dark"
                      label="Address*"
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="string"
                      name={`${itemPath}.addressDetails.address2`}
                      mode="dark"
                      label="Apt, unit, building, etc."
                      disabled={disabled}
                      onChange={onChangeActive}
                    />
                    <Field
                      xs={4}
                      type="string"
                      name={`${itemPath}.addressDetails.city`}
                      mode="dark"
                      label="City*"
                      disabled={disabled}
                      onChange={onChangeActive}
                    />
                    <Field
                      xs={4}
                      type="state"
                      name={`${itemPath}.addressDetails.state`}
                      mode="dark"
                      label="State*"
                      disabled={disabled}
                      onChange={onChangeActive}
                    />
                    <Field
                      xs={4}
                      type="string"
                      name={`${itemPath}.addressDetails.zip`}
                      mode="dark"
                      label="Zip code*"
                      disabled={disabled}
                      onChange={onChangeActive}
                    />
                  </>
                )}

                {coverageRadio}
              </CollapsibleListItem>
            );
          })}

          <div className={classes.addFormContainer}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  value={currentValue}
                  {...inputValueProp}
                  id="occupiedPropertySearch"
                  name="occupiedPropertySearch"
                  options={filteredOptions}
                  onSelection={handleSelectChange}
                  onBlur={handleBlur}
                  showDeleteIcon
                  showChevronIcon
                  classes={{ option: classes.autocompleteOption }}
                  renderOption={(option) => (
                    <span className={option.id === NOT_ON_POLICY_ID ? classes.notOnPolicyOption : undefined}>
                      {option.text}
                    </span>
                  )}
                  InputComponent={TextField}
                  inputProps={{
                    label: 'Property search',
                    mode: 'dark',
                    onChange: handleInputChange,
                    disabled,
                    helperText: 'Search for an item on the policy',
                    FormHelperTextProps: { className: classes.autocompleteHelperText }
                  }}
                />
              </Grid>

              {selectedProperty && selectedProperty.id === NOT_ON_POLICY_ID && (
                <Grid item xs={12}>
                  <Formik
                    initialValues={NOT_ON_POLICY_INITIAL_VALUES}
                    validateOnChange={false}
                    validateOnBlur={false}
                    validate={(formValues) => {
                      const schema = VALIDATION_SCHEMAS_BY_TYPE[formValues.type];
                      if (!schema) {
                        return {};
                      }
                      try {
                        schema.validateSync(formValues, { abortEarly: false, context: formValues });
                        return {};
                      } catch (err) {
                        let errors = {};
                        err.inner.forEach((error) => {
                          if (error.path) {
                            errors = setIn(errors, error.path, error.message);
                          }
                        });
                        return errors;
                      }
                    }}
                    onSubmit={handleNotOnPolicySubmit}
                  >
                    {({ values: formValues, setFieldValue: setInnerFieldValue, resetForm, submitForm }) => {
                      const legalDescriptionFormat = formValues?.legalDescription?.format;
                      const farmState = formValues?.address?.state || Object.keys(appConfig.farm || {})[0] || 'IL';

                      return (
                        <Grid container spacing={2}>
                          <Field
                            xs={12}
                            type="select"
                            name="type"
                            mode="dark"
                            label="Type of item"
                            options={typeSelectOptions}
                            onChange={(value) => {
                              resetForm({ values: { ...NOT_ON_POLICY_INITIAL_VALUES, type: value } });
                            }}
                            disabled={disabled}
                            className={classes.notOnPolicyTypeSelect}
                          />
                          <Grid item xs={12} className={classes.notOnPolicyFieldsContainer}>
                            <Grid container spacing={2}>
                              {formValues.type === 'location' && (
                                <>
                                  <Field
                                    xs={4}
                                    type="select"
                                    name="legalDescription.format"
                                    mode="dark"
                                    label="Legal description format*"
                                    options={legalDescriptionFormatOptions}
                                    onChange={(value) => {
                                      if (value === 'section-township-range') {
                                        setInnerFieldValue('legalDescription.townshipCoordinates', 'unknown');
                                        setInnerFieldValue('legalDescription.rangeCoordinates', 'unknown');
                                      }
                                    }}
                                    disabled={disabled}
                                  />
                                  <Field
                                    xs={4}
                                    type="numeric"
                                    name="numberOfAcres"
                                    mode="dark"
                                    label="Acres*"
                                    disabled={disabled}
                                  />
                                  <Field
                                    xs={4}
                                    type="string"
                                    name="description"
                                    mode="dark"
                                    label="Location name*"
                                    disabled={disabled}
                                  />

                                  {legalDescriptionFormat === 'street' && (
                                    <>
                                      <AddressAutocomplete
                                        id="address.address"
                                        name="address.address"
                                        xs={6}
                                        mode="dark"
                                        label="Address*"
                                        disabled={disabled}
                                      />
                                      <Field
                                        xs={6}
                                        type="string"
                                        name="address.address2"
                                        mode="dark"
                                        label="Apt, unit, building, etc."
                                        disabled={disabled}
                                      />
                                      <Field
                                        xs={4}
                                        type="string"
                                        name="address.city"
                                        mode="dark"
                                        label="City*"
                                        disabled={disabled}
                                      />
                                      <Field
                                        xs={4}
                                        type="state"
                                        name="address.state"
                                        mode="dark"
                                        label="State*"
                                        disabled={disabled}
                                      />
                                      <Field
                                        xs={4}
                                        type="string"
                                        name="address.zip"
                                        mode="dark"
                                        label="Zip code*"
                                        disabled={disabled}
                                      />
                                    </>
                                  )}

                                  {legalDescriptionFormat === 'section-township-range' && (
                                    <>
                                      <Field
                                        xs={4}
                                        type="string"
                                        name="address.city"
                                        mode="dark"
                                        label="City*"
                                        disabled={disabled}
                                      />
                                      <Field
                                        xs={4}
                                        type="state"
                                        name="address.state"
                                        mode="dark"
                                        label="State*"
                                        disabled={disabled}
                                      />
                                      <Field
                                        xs={4}
                                        type="string"
                                        name="address.zip"
                                        mode="dark"
                                        label="Zip code*"
                                        disabled={disabled}
                                      />
                                      <CountiesAutocomplete
                                        id="address.county"
                                        state={farmState}
                                        setValues={setInnerFieldValue}
                                        mode="dark"
                                        disabled={disabled}
                                      />
                                      <Field
                                        xs={4}
                                        type="string"
                                        name="legalDescription.section"
                                        mode="dark"
                                        label="Section*"
                                        disabled={disabled}
                                      />
                                      <Field
                                        xs={2}
                                        type="string"
                                        name="legalDescription.township"
                                        mode="dark"
                                        label="Township*"
                                        disabled={disabled}
                                      />
                                      <Field
                                        xs={2}
                                        type="select"
                                        name="legalDescription.townshipCoordinates"
                                        mode="dark"
                                        label="Coordinates"
                                        options={coordinateOptions}
                                        disabled={disabled}
                                      />
                                      <Field
                                        xs={2}
                                        type="string"
                                        name="legalDescription.range"
                                        mode="dark"
                                        label="Range*"
                                        disabled={disabled}
                                      />
                                      <Field
                                        xs={2}
                                        type="select"
                                        name="legalDescription.rangeCoordinates"
                                        mode="dark"
                                        label="Coordinates"
                                        options={coordinateOptions}
                                        disabled={disabled}
                                      />
                                    </>
                                  )}

                                  {legalDescriptionFormat === 'lot-block' && (
                                    <>
                                      <Field
                                        xs={6}
                                        type="numeric"
                                        name="legalDescription.lot"
                                        mode="dark"
                                        label="Lot*"
                                        disabled={disabled}
                                      />
                                      <Field
                                        xs={6}
                                        type="numeric"
                                        name="legalDescription.block"
                                        mode="dark"
                                        label="Block*"
                                        disabled={disabled}
                                      />
                                      <Field
                                        xs={4}
                                        type="string"
                                        name="address.city"
                                        mode="dark"
                                        label="City*"
                                        disabled={disabled}
                                      />
                                      <Field
                                        xs={4}
                                        type="state"
                                        name="address.state"
                                        mode="dark"
                                        label="State*"
                                        disabled={disabled}
                                      />
                                      <Field
                                        xs={4}
                                        type="string"
                                        name="address.zip"
                                        mode="dark"
                                        label="Zip code*"
                                        disabled={disabled}
                                      />
                                      <CountiesAutocomplete
                                        id="address.county"
                                        state={farmState}
                                        setValues={setInnerFieldValue}
                                        mode="dark"
                                        disabled={disabled}
                                      />
                                    </>
                                  )}

                                  {legalDescriptionFormat === 'long-legal' && (
                                    <>
                                      <Field
                                        xs={4}
                                        type="string"
                                        name="address.city"
                                        mode="dark"
                                        label="City*"
                                        disabled={disabled}
                                      />
                                      <Field
                                        xs={4}
                                        type="state"
                                        name="address.state"
                                        mode="dark"
                                        label="State*"
                                        disabled={disabled}
                                      />
                                      <Field
                                        xs={4}
                                        type="string"
                                        name="address.zip"
                                        mode="dark"
                                        label="Zip code*"
                                        disabled={disabled}
                                      />
                                      <CountiesAutocomplete
                                        id="address.county"
                                        state={farmState}
                                        setValues={setInnerFieldValue}
                                        mode="dark"
                                        disabled={disabled}
                                      />
                                    </>
                                  )}

                                  {!!legalDescriptionFormat && (
                                    <>
                                      <Field
                                        xs={12}
                                        type="string"
                                        name="additionalLegalDescription"
                                        mode="dark"
                                        label="Additional legal description (optional)"
                                        disabled={disabled}
                                      />
                                      <Field
                                        xs={12}
                                        type="string"
                                        name="locationDirections"
                                        mode="dark"
                                        label="Location directions (optional)"
                                        disabled={disabled}
                                      />
                                    </>
                                  )}
                                </>
                              )}
                              {formValues.type === 'residence' && (
                                <>
                                  <Field
                                    xs={12}
                                    type="string"
                                    name="description"
                                    mode="dark"
                                    label="Item name*"
                                    helperText="Give this item a small name for reference"
                                    disabled={disabled}
                                  />
                                  <AddressAutocomplete
                                    id="address.address"
                                    name="address.address"
                                    xs={6}
                                    mode="dark"
                                    label="Address*"
                                    disabled={disabled}
                                  />
                                  <Field
                                    xs={6}
                                    type="string"
                                    name="address.address2"
                                    mode="dark"
                                    label="Apt, unit, building, etc."
                                    disabled={disabled}
                                  />
                                  <Field
                                    xs={4}
                                    type="string"
                                    name="address.city"
                                    mode="dark"
                                    label="City*"
                                    disabled={disabled}
                                  />
                                  <Field
                                    xs={4}
                                    type="state"
                                    name="address.state"
                                    mode="dark"
                                    label="State*"
                                    disabled={disabled}
                                  />
                                  <Field
                                    xs={4}
                                    type="string"
                                    name="address.zip"
                                    mode="dark"
                                    label="Zip code*"
                                    disabled={disabled}
                                  />
                                </>
                              )}
                            </Grid>
                          </Grid>
                          {formValues.type && (
                            <>
                              <Grid item xs={12}>
                                <div className={classes.coverageLabel}>Coverage</div>
                              </Grid>
                              <Field
                                xs={12}
                                type="radio"
                                id="coverage"
                                name="coverage"
                                mode="dark"
                                options={COVERAGE_OPTIONS}
                                disabled={disabled}
                                labelClassName={classes.coverageRadioLabel}
                                optionsSideBySide={false}
                              />
                              <Grid container alignItems="center" className={classes.addButtonRow}>
                                <Button
                                  type="button"
                                  mode="big"
                                  variant="contained"
                                  color="secondary"
                                  className={classes.addButton}
                                  disabled={disabled}
                                  onClick={submitForm}
                                >
                                  Add
                                </Button>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      );
                    }}
                  </Formik>
                </Grid>
              )}

              {selectedProperty && selectedProperty.id !== NOT_ON_POLICY_ID && (
                <Grid item xs={12}>
                  <Formik
                    key={selectedProperty.id}
                    initialValues={{
                      coverage: DEFAULT_COVERAGE
                    }}
                    validateOnChange={false}
                    validateOnBlur={false}
                    onSubmit={(formValues) => {
                      const newItem = {
                        id: selectedProperty.riskId || selectedProperty.locationId,
                        type: selectedProperty.type,
                        locationId: selectedProperty.locationId,
                        riskId: selectedProperty.riskId,
                        coverage: formValues.coverage,
                        locationLinkId: selectedProperty.locationLinkId,
                        riskLinkId: selectedProperty.riskLinkId,
                        description: selectedProperty.description,
                        addressDetails: selectedProperty.addressDetails,
                        ...(selectedProperty.type === 'location' && {
                          legalDescription: selectedProperty.legalDescription,
                          additionalLegalDescription: selectedProperty.additionalLegalDescription,
                          locationDirections: selectedProperty.locationDirections
                        })
                      };
                      setFieldValue(FIELD_NAME, [...allItems, newItem]);
                      setFieldTouched(FIELD_NAME);
                      setSelectedProperty(null);
                      setInputValue('');
                      onChangeActive();
                    }}
                  >
                    {({ submitForm }) => (
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <div className={classes.coverageLabel}>Coverage</div>
                        </Grid>
                        <Field
                          xs={12}
                          type="radio"
                          id="coverage"
                          name="coverage"
                          mode="dark"
                          options={COVERAGE_OPTIONS}
                          disabled={disabled}
                          labelClassName={classes.coverageRadioLabel}
                        />
                        <Grid container alignItems="center" className={classes.addButtonRow}>
                          <Button
                            type="button"
                            mode="big"
                            variant="contained"
                            color="secondary"
                            className={classes.addButton}
                            disabled={disabled}
                            onClick={submitForm}
                          >
                            Add
                          </Button>
                        </Grid>
                      </Grid>
                    )}
                  </Formik>
                </Grid>
              )}
            </Grid>
          </div>
        </FormSectionWrapper>
      </SubRiskCollapsibleSection>
    </div>
  );
};

OccupiedAdditionalPropertiesExtensions.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onChangeActive: PropTypes.func.isRequired
};

export default OccupiedAdditionalPropertiesExtensions;
